import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        this.#applyCartSectionUpdate(parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#refreshDrawerCartInPlace();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#applyCartSectionUpdate(cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#refreshDrawerCartInPlace();
    }
  };

  /**
   * Fetch section HTML and apply without replacing an open modal dialog.
   */
  async #refreshDrawerCartInPlace() {
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      if (!this.isConnected) return;
      this.#applyCartSectionUpdate(html);
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Updates cart UI from section HTML without destroying an open cart-drawer dialog.
   * Full-section morph of the header while `showModal()` is active leaves the page inert.
   * @param {string} cartItemsHtml
   */
  #applyCartSectionUpdate(cartItemsHtml) {
    const doc = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
    const nextCartItems = doc.querySelector('cart-items-component');
    const liveDialog = this.closest('dialog.cart-drawer__dialog');
    const inDrawer = Boolean(this.closest('cart-drawer-component'));

    if (nextCartItems && inDrawer) {
      // Close nested quick-add modal before touching the open cart drawer.
      const quickAddDialogEl = document.querySelector('quick-add-dialog > dialog');
      if (quickAddDialogEl instanceof HTMLDialogElement && quickAddDialogEl.open) {
        quickAddDialogEl.classList.remove('dialog-closing');
        try {
          quickAddDialogEl.close();
        } catch {
          // ignore
        }
      }

      const nextDialog = doc.querySelector('dialog.cart-drawer__dialog');
      const nextIsEmpty = nextDialog?.classList.contains('cart-drawer--empty');
      const liveIsEmpty = liveDialog?.classList.contains('cart-drawer--empty');
      const drawerWasOpen = Boolean(liveDialog?.open);

      // Drawer already visible (e.g. opened on add) — freeze enter animation before morph/reseat.
      // Use the settled class only (CSS). Do NOT set inline animation:none — it survives close
      // and kills enter/exit transitions until a full page reload.
      if (drawerWasOpen && liveDialog) {
        liveDialog.classList.add('cart-drawer--settled');
      }

      // Empty <-> non-empty swaps the whole drawer tree; targeted morph is not enough.
      if (nextIsEmpty || liveIsEmpty) {
        morph(this, nextCartItems);
      } else {
        // Avoid morphing the header/close button. Leave recommendations mounted.
        this.#syncDrawerHeaderCount(nextCartItems);
        this.#morphDrawerRegion('.cart-drawer__free-shipping', nextCartItems);
        this.#morphDrawerRegion('.cart-drawer__items', nextCartItems);
        this.#morphDrawerRegion('.cart-drawer__summary', nextCartItems);
      }

      if (liveDialog && nextDialog) {
        liveDialog.classList.toggle('cart-drawer--empty', nextDialog.classList.contains('cart-drawer--empty'));
        const labelledBy = nextDialog.getAttribute('aria-labelledby');
        if (labelledBy) liveDialog.setAttribute('aria-labelledby', labelledBy);
      }

      if (liveDialog?.open) {
        document.documentElement.setAttribute('data-dialog-open', '');
        if (document.body.style.position !== 'fixed') {
          document.body.style.width = '100%';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${window.scrollY}px`;
        }
        // Morphing can kick showModal() off the top layer. close()+showModal()
        // while already :modal freezes Safari iOS (inert page, cannot close).
        if (!liveDialog.matches(':modal')) {
          this.#reseatOpenDrawerModal(liveDialog);
        }
      }

      return;
    }

    morphSection(this.sectionId, cartItemsHtml);
  }

  /**
   * @param {string} selector
   * @param {Element} nextCartItems
   */
  #morphDrawerRegion(selector, nextCartItems) {
    const liveRegion = this.querySelector(selector);
    const nextRegion = nextCartItems.querySelector(selector);
    if (liveRegion && nextRegion) {
      morph(liveRegion, nextRegion);
    }
  }

  /**
   * Update cart count in the open drawer header without morphing the close button.
   * @param {Element} nextCartItems
   */
  #syncDrawerHeaderCount(nextCartItems) {
    const liveCount = this.querySelector('.cart-drawer__heading-count');
    const nextCount = nextCartItems.querySelector('.cart-drawer__heading-count');
    const liveStatus = this.querySelector('.cart-drawer__header [role="status"]');
    const nextStatus = nextCartItems.querySelector('.cart-drawer__header [role="status"]');

    if (liveCount && nextCount) {
      liveCount.textContent = nextCount.textContent;
    } else if (liveCount && !nextCount) {
      liveCount.remove();
    } else if (!liveCount && nextCount) {
      const liveText = this.querySelector('.cart-drawer__heading-text');
      if (liveText) liveText.appendChild(nextCount.cloneNode(true));
    }

    if (liveStatus && nextStatus) {
      liveStatus.textContent = nextStatus.textContent;
    }
  }

  /**
   * Restore body scroll lock after a top-layer reseat.
   * @param {number} scrollY
   */
  #restoreDrawerScrollLock(scrollY) {
    document.documentElement.setAttribute('data-dialog-open', '');
    document.documentElement.dataset.scrollLockY = String(scrollY);
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  /**
   * DOM updates inside showModal() can drop the dialog from the top layer (clicks die).
   * Only call this when `dialog.open && !dialog.matches(':modal')`.
   * @param {HTMLDialogElement} dialog
   */
  #reseatOpenDrawerModal(dialog) {
    if (dialog.matches(':modal')) return;

    dialog.classList.add('cart-drawer--settled');
    dialog.classList.remove('dialog-closing');
    dialog.style.setProperty('animation', 'none', 'important');
    dialog.style.setProperty('transition', 'none', 'important');

    const fromDataset = Number.parseInt(document.documentElement.dataset.scrollLockY || '', 10);
    const fromTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10));
    const scrollY = Number.isFinite(fromDataset)
      ? fromDataset
      : fromTop || window.scrollY;

    const reopen = () => {
      try {
        if (!dialog.open || !dialog.matches(':modal')) {
          if (dialog.open) dialog.close();
          dialog.showModal();
        }
      } catch {
        // ignore
      }

      if (!dialog.matches(':modal')) {
        try {
          const probe = document.createElement('dialog');
          probe.setAttribute('aria-hidden', 'true');
          probe.style.cssText =
            'position:fixed;width:0;height:0;padding:0;border:0;overflow:hidden;visibility:hidden;';
          document.body.appendChild(probe);
          probe.showModal();
          probe.close();
          probe.remove();
          dialog.showModal();
        } catch {
          // ignore
        }
      }

      this.#restoreDrawerScrollLock(scrollY);
    };

    if (dialog.open) {
      const onClose = () => {
        dialog.removeEventListener('close', onClose);
        reopen();
      };
      dialog.addEventListener('close', onClose, { once: true });
      try {
        dialog.close();
      } catch {
        dialog.removeEventListener('close', onClose);
        reopen();
      }
    } else {
      reopen();
    }

    requestAnimationFrame(() => {
      dialog.style.removeProperty('animation');
      dialog.style.removeProperty('transition');
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
